import React from "react";
import { Modal, TextField, Toggle } from "@fluentui/react";
import { SearchIcon, AddIcon, ClearIcon } from "@fluentui/react-icons";
import * as S from "../Styles/ManageCompositionStyles";
import FundService from "../../../../services/FundService";
import { IManageCompositionProps, ISearchOption, ISelectedFund, IPortfolioModelModificationState } from "../Interfaces/SummaryPortfolioInterfaces";
import { validateCompositionDateRange } from "../Functions/CompositionDateValidation";
import { convertDateToISOForDateNavigator } from "../Functions/ConvertToDateISOForDateNavigator";

// Funciones auxiliares para obtener fechas en formato ISO (fuera del componente para evitar re-renders)
const getTodayISO = (): string =>
{
    const today = new Date();
    const isoDate = today.toISOString().split('T')[0];
    return isoDate;
};

const getYesterdayISO = (): string =>
{
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const isoDate = yesterday.toISOString().split('T')[0];
    return isoDate;
};

const ManageComposition: React.FC<IManageCompositionProps> = ({isOpen, onClose, onSave, mode, portfolioModelName, portfolioModelId, isVirtualComposition, isLoading = false, currentCompositionData, existingCompositions = []}) =>
{
    // isVirtualComposition: Parámetro oculto que indica si estamos editando la composición virtual del día de hoy
    // Sirve para realizar una llamada diferente. Si es true, estamos editando la cartera modelo. Si es false o undefined,
    // estamos editando una composición anterior.
    
    const [searchQuery, setSearchQuery] = React.useState<string>('');
    const [searchResults, setSearchResults] = React.useState<ISearchOption[]>([]);
    const [selectedFunds, setSelectedFunds] = React.useState<ISelectedFund[]>([]);
    const [isSearching, setIsSearching] = React.useState<boolean>(false);
    const [showSearchResults, setShowSearchResults] = React.useState<boolean>(false);
    const [date, setDate] = React.useState<string>('');
    
    const [portfolioModelModification, setPortfolioModelModification] = React.useState<IPortfolioModelModificationState>(
    {
        userRequestPortfolioModelChange: false,
        selectedCompositionDate: null,
        validationInfo: null
    });

    // Referencia para cancelar búsquedas pendientes
    const searchTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
    
    // Referencia para controlar si ya se inicializó la fecha en este modal
    const dateInitializedRef = React.useRef<boolean>(false);

    // Inicializamos la fecha según el modo SOLO cuando se abre el modal por primera vez
    React.useEffect(() =>
    {
        // Solo inicializar la fecha cuando el modal se abre (isOpen = true) y no se ha inicializado aún
        if(!isOpen)
        {
            // Resetear el flag cuando se cierra el modal
            dateInitializedRef.current = false;
            return;
        }
        
        // Si ya se inicializó la fecha en este modal, no volver a inicializar
        if(dateInitializedRef.current)
        {
            return;
        }
        
        if(mode === 'add')
        {
            // En modo 'add', inicializamos con ayer (máxima fecha permitida, sólo fechas del pasado)
            setDate(getYesterdayISO());
            dateInitializedRef.current = true;
        }
        else if(mode === 'edit' && currentCompositionData?.date)
        {
            // En modo 'edit', inicializamos con la fecha de la composición actual
            const isoDate = convertDateToISOForDateNavigator(currentCompositionData.date);
            if(isoDate)
            {
                setDate(isoDate);
                dateInitializedRef.current = true;
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]); // SOLO ejecutar cuando cambie isOpen, no en cada render

    // Validamos fecha cuando cambie (solo en modo 'add')
    React.useEffect(() =>
    {
        // Solo validar si el modal está abierto, la fecha ya fue inicializada y es modo 'add'
        if(mode === 'add' && date && existingCompositions.length > 0 && isOpen && dateInitializedRef.current)
        {
            const validation = validateCompositionDateRange(date, existingCompositions);
            
            setPortfolioModelModification(prev =>
            ({
                ...prev,
                selectedCompositionDate: date,
                validationInfo: validation,
                userRequestPortfolioModelChange: validation.isInRange ? prev.userRequestPortfolioModelChange : false
            }));
        }
        else if(mode === 'add')
        {
            // Reseteamos estado si no hay composiciones o no hay fecha
            setPortfolioModelModification(prev =>
            ({
                ...prev,
                selectedCompositionDate: date,
                validationInfo: null,
                userRequestPortfolioModelChange: false
            }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date, mode, isOpen]);

    // Validar cuando se carguen las composiciones existentes (solo en modo 'add')
    React.useEffect(() =>
    {
        if(mode === 'add' && date && existingCompositions.length > 0)
        {
            const validation = validateCompositionDateRange(date, existingCompositions);
            
            setPortfolioModelModification(prev =>
            ({
                ...prev,
                selectedCompositionDate: date,
                validationInfo: validation,
                userRequestPortfolioModelChange: validation.isInRange ? prev.userRequestPortfolioModelChange : false
            }));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [existingCompositions.length]);

    // Cargar datos de la composición cuando esté en modo 'edit'
    React.useEffect(() =>
    {
        if(mode === 'edit' && currentCompositionData && isOpen)
        {
            // Convertir los datos de la composición actual al formato esperado por el componente
            const fundsToLoad: ISelectedFund[] = currentCompositionData.funds.map(fund =>
            ({
                isin: fund.isin,
                name: fund.name,
                finametrixId: fund.finametrixId || '',
                allFundsId: fund.allFundsId?.toString() || '',
                percentage: fund.weight,
                category: '', // No disponible en los datos actuales
                available: true, // Asumimos que están disponibles, lo comprobamos al intentar editar.
                subCategory: '', // No disponible en los datos actuales
                cleanShare: false // No disponible en los datos actuales
            }));

            setSelectedFunds(fundsToLoad);
        }
        else if(mode === 'add' && isOpen)
        {
            // Limpiar los fondos seleccionados cuando se abre en modo 'add'
            setSelectedFunds([]);
        }
    }, [mode, currentCompositionData, isOpen]);

    // Función para manejar el cambio de fecha
    const handleDateChange = React.useCallback((event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) =>
    {
        const selectedDate = newValue || '';
        
        // Solo cambiar la fecha si es una fecha válida y completa (formato YYYY-MM-DD)
        if(!selectedDate || selectedDate.length !== 10 || !selectedDate.match(/^\d{4}-\d{2}-\d{2}$/))
        {
            return;
        }
        
        const today = getTodayISO();
        
        if(mode === 'add')
        {
            // En modo 'add', solo permitimos fechas pasadas (máximo ayer)
            const yesterday = getYesterdayISO();
            
            if(selectedDate <= yesterday)
            {
                setDate(selectedDate);
            }
        }
        else
        {
            // En modo 'edit', permitir fechas que no sean futuras (incluyendo hoy)
            if(selectedDate <= today)
            {
                setDate(selectedDate);
            }
        }
    }, [mode]);

    // Función para manejar el cambio del checkbox de modificación de Cartera Modelo
    const handlePortfolioModelModificationChange = React.useCallback((event: React.MouseEvent<HTMLElement>, checked?: boolean) =>
    {
        setPortfolioModelModification(prev =>
        ({
            ...prev,
            userRequestPortfolioModelChange: checked || false
        }));
    }, []);

    // Función para manejar la búsqueda
    const handleSearchChange = React.useCallback((event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) =>
    {
        const query = newValue || '';
        setSearchQuery(query);
        
        // Cancelar búsqueda anterior si existe
        if(searchTimeoutRef.current)
        {
            clearTimeout(searchTimeoutRef.current);
        }
        
        if(query.length >= 3)
        {
            setIsSearching(true);
            setShowSearchResults(true);
            
            // Realizar búsqueda con delay
            searchTimeoutRef.current = setTimeout(async () =>
            {
                try
                {
                    const results = await FundService.getSearchFundsForComposition(query);
                    setSearchResults(results);
                }
                catch(error)
                {
                    console.error('Error searching funds:', error);
                    setSearchResults([]);
                }
                finally
                {
                    setIsSearching(false);
                }
            }, 300);
        }
        else
        {
            setSearchResults([]);
            setShowSearchResults(false);
        }
    }, []);

    // Función para limpiar el input de búsqueda
    const handleClearSearch = React.useCallback(() =>
    {
        setSearchQuery('');
        setSearchResults([]);
        setShowSearchResults(false);
    }, []);

    // Función para seleccionar un fondo
    const handleFundSelect = React.useCallback((fund: ISearchOption) =>
    {
        // Verificar si el fondo ya está seleccionado
        const isAlreadySelected = selectedFunds.some(selected => selected.isin === fund.isin);
        
        if(!isAlreadySelected)
        {
            const newSelectedFund: ISelectedFund =
            {
                isin: fund.isin,
                name: fund.name,
                finametrixId: fund.finametrixId,
                allFundsId: fund.allFundsId,
                percentage: 0,
                category: fund.category,
                available: fund.available,
                subCategory: fund.subCategory,
                cleanShare: fund.cleanShare
            };
            
            setSelectedFunds(prev => [...prev, newSelectedFund]);
        }
        
        // Limpiar búsqueda
        setSearchQuery('');
        setSearchResults([]);
        setShowSearchResults(false);
    }, [selectedFunds]);

    // Función para actualizar el porcentaje de un fondo
    const handlePercentageChange = React.useCallback((fundIsin: string, newPercentage: number) =>
    {
        setSelectedFunds
        (
            prev => prev.map(fund => fund.isin === fundIsin ? {...fund, percentage: Math.max(0, Math.min(100, newPercentage))} : fund
        ));
    }, []);

    // Función para eliminar un fondo
    const handleRemoveFund = React.useCallback((fundIsin: string) =>
    {
        setSelectedFunds(prev => prev.filter(fund => fund.isin !== fundIsin));
    }, []);

    // Función para calcular el total de porcentajes
    const totalPercentage = React.useMemo(() =>
    {
        return selectedFunds.reduce((total, fund) => total + fund.percentage, 0);
    }, [selectedFunds]);

    // Función para manejar el guardado
    const handleSave = React.useCallback(() =>
    {
        if(selectedFunds.length === 0 || isLoading)
        {
            return;
        }

        const compositionData =
        {
            mode,
            portfolioModelId,
            portfolioModelName,
            funds: selectedFunds,
            totalPercentage,
            isValid: Math.abs(totalPercentage - 100) < 0.01,
            ...(mode === 'add' && {date}),
            ...(mode === 'add' && portfolioModelModification.userRequestPortfolioModelChange && {userRequestPortfolioModelChange: true})
        };

        onSave(compositionData);
        
        // NO reseteamos la fecha aquí - se maneja en el componente padre al cerrar el modal
    }, [mode, portfolioModelId, portfolioModelName, selectedFunds, totalPercentage, date, onSave, isLoading, portfolioModelModification.userRequestPortfolioModelChange]);

    // Función para limpiar el modal al cerrar
    const handleClose = React.useCallback(() =>
    {
        // No permitimos cerrar durante la carga
        if(isLoading)
        {
            return;
        }

        // Cancelar búsqueda pendiente
        if(searchTimeoutRef.current)
        {
            clearTimeout(searchTimeoutRef.current);
        }
        
        setSearchQuery('');
        setSearchResults([]);
        setShowSearchResults(false);
        setSelectedFunds([]);

        // NO reseteamos la fecha aquí - se maneja en el componente padre al cerrar el modal
        
        onClose();
    }, [onClose, isLoading]);

    // Función para establecer el máximo porcentaje restante para completar el 100%
    const handleMaxPercentage = React.useCallback((fundIsin: string) =>
    {
        setSelectedFunds(prev => 
        {
            // Calcular el total de porcentajes excluyendo el fondo actual
            const totalWithoutCurrentFund = prev.filter(fund => fund.isin !== fundIsin).reduce((sum, fund) => sum + fund.percentage, 0);
            
            // El porcentaje máximo es lo que falta para llegar a 100%
            const maxPercentage = Math.max(0, 100 - totalWithoutCurrentFund);
            
            // Redondear a 2 decimales (centésimas)
            const roundedMaxPercentage = Math.round(maxPercentage * 100) / 100;
            
            return prev.map(fund => fund.isin === fundIsin ? {...fund, percentage: roundedMaxPercentage} : fund);
        });
    }, []);

    // Determinar si se puede guardar
    const canSave = React.useMemo(() =>
    {
        // No se puede guardar durante la carga
        if(isLoading)
        {
            return false;
        }

        // Condiciones básicas para ambos modos
        const hasFunds = selectedFunds.length > 0;
        const isPercentageValid = Math.abs(totalPercentage - 100) < 0.01;
        const allFundsHaveWeight = selectedFunds.every(fund => fund.percentage >= 0.01);
        
        if(mode === 'add')
        {
            // Condiciones específicas para modo 'add'
            const hasAtLeastOneFund = hasFunds;
            const percentageIsValid = isPercentageValid && allFundsHaveWeight;
            
            return hasAtLeastOneFund && percentageIsValid;
        }
        else
        {
            // Condiciones para modo 'edit' (comportamiento original)
            return hasFunds && isPercentageValid;
        }
    }, [selectedFunds, totalPercentage, mode, isLoading]);

    const modalTitle = mode === 'add' ? `Añadir Composición - ${portfolioModelName || 'Cartera Modelo'}` : `Editar Composición - ${portfolioModelName || 'Cartera Modelo'}`;

    const actionButtonText = React.useMemo(() =>
    {
        if(isLoading)
        {
            return mode === 'add' ? 'Añadiendo...' : 'Guardando...';
        }
        return mode === 'add' ? 'Añadir Composición' : 'Editar Composición';
    }, [mode, isLoading]);

    // Mensaje de validación para modo 'add'
    const getValidationMessage = React.useCallback(() =>
    {
        if(mode !== 'add')
        {
            return null;
        }

        const hasFunds = selectedFunds.length > 0;
        const isPercentageValid = Math.abs(totalPercentage - 100) < 0.01;
        const allFundsHaveWeight = selectedFunds.every(fund => fund.percentage >= 0.01);

        if(!hasFunds)
        {
            return 'Debes añadir al menos un fondo a la composición.';
        }
        
        if(!allFundsHaveWeight)
        {
            return 'Todos los fondos deben tener al menos 0,01% de peso.';
        }
        
        if(!isPercentageValid)
        {
            return 'El peso total de los fondos debe sumar exactamente 100%.';
        }

        return null;
    }, [mode, selectedFunds, totalPercentage]);

    return(
        <React.Fragment>
            <Modal isOpen={isOpen} onDismiss={isLoading ? undefined : handleClose} isBlocking={true} containerClassName="modal-container" styles={{main: {width: '70vw !important', maxWidth: '1600px', height: 'auto'}}}>
                <S.ModalContainer>
                    <S.ModalTitle>{modalTitle}</S.ModalTitle>
                    <S.ContentContainer>
                        <S.InfoRow>
                            <S.InfoText>
                                {mode === 'add' 
                                    ? 'Selecciona la fecha pasada en la que vas a introducir los fondos y sus pesos para crear una nueva composición de cartera. Esta fecha no puede ser el día de hoy. Si quieres modificar la cartera modelo actual, selecciona el botón de editar.'
                                    : 'Modifica los fondos y pesos correspondientes de la composición existente.'
                                }
                            </S.InfoText>
                        </S.InfoRow>
                        {mode === 'add' && portfolioModelModification.validationInfo?.isInRange &&
                        (
                            <S.PortfolioModelModificationContainer>
                                <S.ModificationToggleContainer>
                                    <S.ModificationLabel>¿Deseas actualizar tu Cartera Modelo?</S.ModificationLabel>
                                    <Toggle checked={portfolioModelModification.userRequestPortfolioModelChange}
                                        onChange={handlePortfolioModelModificationChange}
                                        onText="Sí"
                                        offText="No"
                                        styles={{root: {marginLeft: '12px', marginBottom: '0 !important'}}}
                                    />
                                </S.ModificationToggleContainer>
                                <S.ModificationInfoText>
                                    La fecha seleccionada está en un intervalo entre la última composición de esta Cartera Modelo y el día de hoy.
                                    <br />
                                    <br />
                                    Marcando el SÍ indicas que deseas que el valor de la composición actualice el valor de tu Cartera Modelo. 
                                    Esto afecta a todas las operaciones asociadas a tu Cartera Modelo, así como a las Aportaciones Periódicas 
                                    que esta tuviera a partir de este momento.
                                </S.ModificationInfoText>
                            </S.PortfolioModelModificationContainer>
                        )}
                        {mode === 'add' &&
                        (
                            <S.DateContainer>
                                <S.DateLabel>Fecha de la Composición:</S.DateLabel>
                                <TextField type="date" value={date} onChange={handleDateChange} autoComplete="off"
                                    max={mode === 'add' ? getYesterdayISO() : getTodayISO()}
                                    styles=
                                    {{
                                        root: {width: '200px'},
                                        field:
                                        {
                                            padding: '8px 12px', border: '1px solid #605e5c',
                                            borderRadius: '2px',
                                            outline: 'none'
                                        }
                                    }}
                                />
                            </S.DateContainer>
                        )}
                        <S.SearchContainer>
                            <S.SearchLabel>Buscar Fondos:</S.SearchLabel>
                            <S.SearchInputContainer>
                                <TextField value={searchQuery} onChange={handleSearchChange} autoComplete="off"
                                    placeholder="Escribe el nombre o ISIN del fondo..."
                                    styles=
                                    {{
                                        root: {width: '100%'},
                                        field: {paddingRight: '40px'}
                                    }}
                                />
                                {searchQuery.length > 0 &&
                                (
                                    <S.ClearButton onClick={handleClearSearch} title="Limpiar búsqueda">
                                        <ClearIcon />
                                    </S.ClearButton>
                                )}
                                <S.SearchIconContainer>
                                    {isSearching ?
                                    (
                                        <S.LoadingSpinner />
                                    ) :
                                    (
                                        <SearchIcon />
                                    )}
                                </S.SearchIconContainer>
                            </S.SearchInputContainer>
                            {showSearchResults && isSearching &&
                            (
                                <S.SearchResultsContainer>
                                    <S.SearchingContainer>
                                        <S.LoadingSpinner />
                                        <S.SearchingText>Buscando fondos...</S.SearchingText>
                                    </S.SearchingContainer>
                                </S.SearchResultsContainer>
                            )}
                            {showSearchResults && !isSearching && searchResults.length > 0 &&
                            (
                                <S.SearchResultsContainer>
                                    {searchResults.map((fund) =>
                                    (
                                        <S.SearchResultItem key={fund.isin} onClick={fund.available ? () => handleFundSelect(fund) : undefined} $isAvailable={fund.available}>
                                            <S.SearchResultName>{fund.name} - ISIN: {fund.isin}</S.SearchResultName>
                                            <S.SearchResultDetails>
                                                <span>Categoría: {fund.category}</span>
                                                <span>Subcategoría: {fund.subCategory}</span>
                                                {!fund.available && <S.SpanNotAvailable>No disponible</S.SpanNotAvailable>}
                                            </S.SearchResultDetails>
                                        </S.SearchResultItem>
                                    ))}
                                </S.SearchResultsContainer>
                            )}
                            {showSearchResults && searchResults.length === 0 && !isSearching && searchQuery.length >= 3 &&
                            (
                                <S.NoResultsMessage>
                                    No se encontraron fondos que coincidan con tu búsqueda.
                                </S.NoResultsMessage>
                            )}
                        </S.SearchContainer>
                        {selectedFunds.length > 0 &&
                        (
                            <S.SelectedFundsContainer>
                                <S.SelectedFundsTitle>Fondos Seleccionados:</S.SelectedFundsTitle>
                                <S.FundsTable className={mode === 'edit' ? 'edit-mode' : 'add-mode'}>
                                    <S.TableHeader>
                                        <tr>
                                            <S.TableHeaderCell>Fondo</S.TableHeaderCell>
                                            <S.TableHeaderCell>ISIN</S.TableHeaderCell>
                                            {mode !== 'edit' && <S.TableHeaderCell>Subcategoría</S.TableHeaderCell>}
                                            <S.TableHeaderCell>Peso (%)</S.TableHeaderCell>
                                            <S.TableHeaderCell>Acciones</S.TableHeaderCell>
                                        </tr>
                                    </S.TableHeader>
                                    <S.TableBody>
                                        {selectedFunds.map((fund) =>
                                        (
                                            <S.TableRow key={fund.isin}>
                                                <S.TableCell>{fund.name}</S.TableCell>
                                                <S.TableCell>{fund.isin}</S.TableCell>
                                                {mode !== 'edit' && <S.TableCell>{fund.subCategory}</S.TableCell>}
                                                <S.TableCell>
                                                    <S.PercentageInput type="number" value={fund.percentage}
                                                        onChange={(e: any) => handlePercentageChange(fund.isin, parseFloat(e.target.value) || 0)}
                                                        min="0" max="100" step="0.01"
                                                    />
                                                </S.TableCell>
                                                <S.TableCell>
                                                    <S.ActionButtonsContainer>
                                                        <S.MaxButton onClick={() => handleMaxPercentage(fund.isin)}>
                                                            Max
                                                        </S.MaxButton>
                                                        <S.RemoveButton onClick={() => handleRemoveFund(fund.isin)}>
                                                            Eliminar
                                                        </S.RemoveButton>
                                                    </S.ActionButtonsContainer>
                                                </S.TableCell>
                                            </S.TableRow>
                                        ))}
                                    </S.TableBody>
                                </S.FundsTable>
                                <S.PercentageSummary>
                                    <S.PercentageTotal $isValid={Math.abs(totalPercentage - 100) < 0.01}>
                                        Total: {totalPercentage.toFixed(2).replace('.', ',')}%
                                    </S.PercentageTotal>
                                    {Math.abs(totalPercentage - 100) >= 0.01 &&
                                    (
                                        <S.PercentageWarning>
                                            {totalPercentage < 100 
                                                ? `Faltan ${(100 - totalPercentage).toFixed(2).replace('.', ',')}% para completar el 100%`
                                                : `Sobran ${(totalPercentage - 100).toFixed(2).replace('.', ',')}% (máximo 100%)`
                                            }
                                        </S.PercentageWarning>
                                    )}
                                </S.PercentageSummary>
                            </S.SelectedFundsContainer>
                        )}
                        {selectedFunds.length === 0 &&
                        (
                            <S.EmptyStateContainer>
                                <S.EmptyStateIcon>
                                    <AddIcon />
                                </S.EmptyStateIcon>
                                <S.EmptyStateText>
                                    No hay fondos seleccionados. Usa el buscador para añadir fondos a la composición. Puedes añadir tantos como quieras, siempre y cuando el peso total de la composición sume 100%.
                                </S.EmptyStateText>
                            </S.EmptyStateContainer>
                        )}
                    </S.ContentContainer>
                    {mode === 'add' && !canSave && !isLoading &&
                    (
                        <S.ValidationMessage>
                            {getValidationMessage()}
                        </S.ValidationMessage>
                    )}
                    <S.ButtonContainer>
                        <S.CancelButton onClick={handleClose} disabled={isLoading}>
                            Cancelar
                        </S.CancelButton>
                        <S.SaveButton onClick={handleSave} disabled={!canSave}>
                            {actionButtonText}
                        </S.SaveButton>
                    </S.ButtonContainer>
                </S.ModalContainer>
            </Modal>
        </React.Fragment>
    );
};

export default ManageComposition;